import type { FC } from 'react';

import dayjs from 'dayjs';
import type { DateRange } from 'react-day-picker';

import type {
  RangeBoundaryType,
  TimeUnitObject,
  TimeUnitType,
} from '@shared/components/ui/custom/generic-date-range-picker/date-range-picker.utils';
import { TimePickerRow } from '@shared/components/ui/custom/generic-date-range-picker/TimePickerRow';

import { GenericButton } from '../buttons/GenericButton';

interface DatePickerFooterProps {
  dateRange?: DateRange;
  startTime: TimeUnitObject;
  endTime: TimeUnitObject;
  handleTimeChange: (type: RangeBoundaryType, unit: TimeUnitType, value: string) => void;
  handleApply: () => void;
  handleClear: () => void;
  primaryButtonLabel?: string;
}

export const DatePickerFooter: FC<DatePickerFooterProps> = ({
  dateRange,
  startTime,
  endTime,
  handleTimeChange,
  handleApply,
  handleClear,
  primaryButtonLabel = 'Apply',
}) => {
  if (!dateRange?.from) {
    return (
      <div className="flex items-center justify-between w-full h-10 px-1" data-testid="date-picker-footer-no-date">
        <span className="text-sm text-muted-foreground">No date selected</span>
        <div>
          <GenericButton disabled label={primaryButtonLabel} className="h-8" />
          <GenericButton disabled onClick={handleClear} label="Clear" variant="ghost" className="h-8 ml-1" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between w-full h-10 px-1" data-testid="date-picker-footer">
      <TimePickerRow
        dateLabel={dayjs.utc(dateRange.from).format('MMM D YYYY')}
        hourValue={startTime.h}
        minuteValue={startTime.m}
        onHourChange={(v) => handleTimeChange('start', 'h', v)}
        onMinuteChange={(v) => handleTimeChange('start', 'm', v)}
      />
      <span>-</span>
      <TimePickerRow
        dateLabel={dayjs.utc(dateRange.to ?? dateRange.from).format('MMM D YYYY')}
        hourValue={endTime.h}
        minuteValue={endTime.m}
        onHourChange={(v) => handleTimeChange('end', 'h', v)}
        onMinuteChange={(v) => handleTimeChange('end', 'm', v)}
      />
      <div className="ml-2">
        <GenericButton onClick={handleApply} label={primaryButtonLabel} className="h-8" />
        <GenericButton onClick={handleClear} label="Clear" variant="ghost" className="h-8 ml-1" />
      </div>
    </div>
  );
};
